import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useToast } from '../../Utils/toastContext';
import {
  CreditCard,
  ShieldCheck,
  User,
  Calendar,
  Lock,
  Eye,
  EyeOff,
  ScanLine,
  CalendarDays,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Info,
  X,
  ReceiptText,
  FileDown,
  Wallet,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import PricingPlanCards from '../../Components/pricing-plan/pricing-plan-cards';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  ArrowLeftRight,
  LockKeyhole,
  Loader2,
  Users,
  UsersRound,
  UserCheck,
  PlusCircle,
} from 'lucide-react';
import SpendCalendarFilter from './spend-calendar-filter';
import UsageBreakdownPanel from './usage-breakdown-panel';
import UsageByCustomerPanel from './usage-by-customer-panel';
const UsageAccordion = ({
  open,
  onToggle,
  icon: Icon,
  iconWrapClass = 'bg-blue-50 text-[#375AE7]',
  title,
  subtitle,
  badge,
  children,
  variant = 'default',
}) => {
  const isDanger = variant === 'danger';
  return (
    <div
      className={`rounded-[24px] border overflow-hidden shadow-[0_10px_40px_-20px_rgba(15,23,42,0.35)] ${
        isDanger
          ? 'border-red-200 bg-gradient-to-br from-red-50 via-white to-rose-50'
          : 'border-slate-200 bg-white'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`w-full flex items-center gap-3 p-4 sm:p-5 text-left transition ${
          isDanger ? 'hover:bg-red-50/60' : 'hover:bg-slate-50/80'
        }`}
      >
        <div
          className={`flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl ${iconWrapClass}`}
        >
          {Icon ? <Icon size={20} /> : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className={`text-sm sm:text-base font-semibold ${
                isDanger ? 'text-red-700' : 'text-slate-900'
              }`}
            >
              {title}
            </h3>
            {badge}
          </div>
          {subtitle ? (
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 line-clamp-2">
              {subtitle}
            </p>
          ) : null}
        </div>
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
            isDanger ? 'border-red-200 bg-white' : 'border-slate-200 bg-white'
          }`}
        >
          <ChevronDown
            size={16}
            className={`text-slate-500 transition-transform duration-300 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 sm:px-5 sm:pb-5 pt-0 border-t border-slate-100/80">
            <div className="pt-4">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MyPricingPlan = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();
  const billingFilter = localStorage.getItem('billingFilter');

  const [filter, setFilter] = useState(billingFilter || 'spend');
  const [subscription, setSubscription] = useState(null);
  const [mySub, setMySub] = useState(null);
  const [transactions, setTrasansactions] = useState([]);
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardError, setCardError] = useState('');
  const [isDefaultCard, setIsDefaultCard] = useState(false);
  const [cardFocused, setCardFocused] = useState(false);
  const [cvvFocused, setCvvFocused] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  const [microformData, setMicroformData] = useState(null);
  const microformRef = useRef(null);
  const [microformReady, setMicroformReady] = useState(false);
  const location = useLocation();

  const [billingAddress, setBillingAddress] = useState({
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  });
  const [cvvSuccess, setCvvSuccess] = useState(false);

  const [defaultCardError, setDefaultCardError] = useState('');
  const [isBillingDifferent, setIsBillingDifferent] = useState(false);
  const [billingErrors, setBillingErrors] = useState({});
  const [cardSuccess, setCardSuccess] = useState(false);
  const [cvvError, setCvvError] = useState('');
  const [expiryError, setExpiryError] = useState('');
  const [open, setOpen] = useState(false);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pljjans, setPlans] = useState([]);
  const [packageDefaults, setPackageDefaults] = useState(null);
  const [packageDefaultsLoading, setPackageDefaultsLoading] = useState(false);
  const [editPackagesOpen, setEditPackagesOpen] = useState(false);
  const [draftItemCodes, setDraftItemCodes] = useState([]);
  const [savingPackages, setSavingPackages] = useState(false);
  const [resettingPackages, setResettingPackages] = useState(false);
  const cardContainerRef = useRef(null);
  const [transactionPage, setTransactionPage] = useState(1);
  const [totalTransactionPages, setTotalTransactionPages] = useState(1);
  const detectCardBrand = (num) => {
    const digits = num.replace(/\D/g, '');
    if (digits.startsWith('4')) return 'visa';
    if (/^5[1-5]/.test(digits)) return 'mastercard';
    if (digits.startsWith('34') || digits.startsWith('37')) return 'amex';
    return 'unknown';
  };
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [flippedCardId, setFlippedCardId] = useState(null);
  const scrollLeft = () => {
    cardContainerRef.current?.scrollBy({
      left: -320,
      behavior: 'smooth',
    });
  };
  const formatExpiry = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return digits.slice(0, 2) + '/' + digits.slice(2);
  };
  const scrollRight = () => {
    cardContainerRef.current?.scrollBy({
      left: 320,
      behavior: 'smooth',
    });
  };
  const validateCardForm = () => {
    let isValid = true;
    setCardError('');
    setCvvError('');
    setExpiryError('');
    setBillingErrors({});
    setDefaultCardError('');

    if (!cardSuccess) {
      setCardError('Please enter a valid card number');
      isValid = false;
    }
    if (!cvvSuccess) {
      setCvvError('Please enter a valid CVV');
      isValid = false;
    }
    if (!cardExpiry || cardExpiry.length !== 5) {
      setExpiryError('Please enter a valid expiry date');
      isValid = false;
    } else {
      const [month, year] = cardExpiry.split('/');
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear() % 100;
      const currentMonth = currentDate.getMonth() + 1;
      if (
        Number(month) < 1 ||
        Number(month) > 12 ||
        Number(year) < currentYear ||
        (Number(year) === currentYear && Number(month) < currentMonth)
      ) {
        setExpiryError('Card expiry date is invalid');
        isValid = false;
      }
    }
    if (isBillingDifferent) {
      const errors = {};
      if (!billingAddress.address?.trim())
        errors.address = 'Address is required';
      if (!billingAddress.city?.trim()) errors.city = 'City is required';
      if (!billingAddress.state?.trim()) errors.state = 'State is required';
      if (!billingAddress.zip?.trim()) errors.zip = 'ZIP code is required';
      if (!billingAddress.country?.trim())
        errors.country = 'Country is required';
      if (Object.keys(errors).length > 0) {
        setBillingErrors(errors);
        isValid = false;
      }
    }
    return isValid;
  };
  const SuccessTick = () => (
    <div className="animate-in zoom-in duration-300 flex items-center justify-center h-6 w-6 rounded-full bg-emerald-100">
      <CheckCircle2 size={18} className="text-emerald-600" />
    </div>
  );
  const handleSaveCard = () => {
    const microform = microformRef.current?.microform;
    if (!microform) {
      console.error('Microform not initialized');
      showToast('Card form is not ready', 'error');
      return;
    }
    microform.createToken(
      {
        expirationMonth: cardExpiry.substring(0, 2),
        expirationYear: '20' + cardExpiry.substring(3, 5),
      },
      async (err, response) => {
        if (err) {
          console.error('CyberSource Token Error', err);
          return;
        }
        const responseSave = await saveCardAPI(response);
        if (responseSave.status == 201) {
          setShowCardModal(false);
          showToast(responseSave.message, 'success');
          getCreditCard();
          setLoading(false);
        } else {
          showToast(responseSave.message, 'error');
          setLoading(false);
        }
      },
    );
  };
  const saveCardAPI = async (transToken) => {
    const token = localStorage.getItem('jwtToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const apiUrl = `${BASE_URL}/api/v1/agency/subscription/cards`;
    const bodyData = {
      transient_token: transToken,
      is_default: isDefaultCard,
      billing_address: isBillingDifferent
        ? {
            line1: billingAddress.address,
            city: billingAddress.city,
            state: billingAddress.state,
            postal_code: billingAddress.zip,
            country: billingAddress.country,
          }
        : {},
    };
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(bodyData),
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const getCreditCard = async () => {
    try {
      const response = await GetAPIcalls(`/api/v1/agency/subscription/cards`);
      if (response.status == 200) {
        if (response.data) setCards(response.data.cards);
      } else if (response.status === 401) {
        localStorage.removeItem('jwtToken');
        navigate('/login');
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  useEffect(() => {
    if (showCardModal) clearCardForm();
  }, [showCardModal]);
  const clearCardForm = () => {
    setCardExpiry('');
    setBillingAddress({
      address: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    });
    setIsDefaultCard(false);
    setIsBillingDifferent(false);
    setCardError('');
    setCvvError('');
    setExpiryError('');
    setBillingErrors({});
    setCardSuccess(false);
    setCvvSuccess(false);
    setCardFocused(false);
    setCvvFocused(false);
    setSaveAttempted(false);
    setSavingCard(false);
  };
  const makeDefaultCardAPI = async (cardId) => {
    const token = localStorage.getItem('jwtToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const apiUrl = `${BASE_URL}/api/v1/agency/subscription/cards/${cardId}/default`;
    try {
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers,
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const deleteCardAPI = async (cardId) => {
    const token = localStorage.getItem('jwtToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const apiUrl = `${BASE_URL}/api/v1/agency/subscription/cards/${cardId}`;
    try {
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const postApi = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const apiUrl = `${BASE_URL}/api/v1/agency/subscription/microform-session`;
    try {
      const response = await fetch(apiUrl, { method: 'POST', headers });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  useEffect(() => {
    if (showCardModal && microformData?.jwt && window.Flex) {
      initializeMicroform(microformData.jwt);
    }
  }, [showCardModal, microformData]);
  const initializeMicroform = (jwt) => {
    try {
      const flex = new window.Flex(jwt);
      const microform = flex.microform({
        styles: {
          input: {
            'font-size': '16px',
            'font-family': 'Work Sans, sans-serif',
            'letter-spacing': '0.5px',
          },
          ':focus': { color: '#0f172a' },
          '::placeholder': { color: '#b6c2d1', 'font-size': '16px' },
        },
      });
      const number = microform.createField('number', {
        placeholder: 'Card Number',
      });
      const securityCode = microform.createField('securityCode', {
        placeholder: 'CVV',
      });
      number.on('change', function (data) {
        setCardSuccess(data.valid);
        if (data.empty) setCardError('Card number is required');
        else if (!data.valid) setCardError('Invalid card number');
        else setCardError('');
      });
      securityCode.on('change', function (data) {
        setCvvSuccess(data.valid);
        if (data.empty) setCvvError('CVV is required');
        else if (!data.valid) setCvvError('Invalid CVV');
        else setCvvError('');
      });
      number.on('focus', () => setCardFocused(true));
      number.on('blur', () => setCardFocused(false));
      securityCode.on('focus', () => setCvvFocused(true));
      securityCode.on('blur', () => setCvvFocused(false));
      number.load('#card-number-container');
      securityCode.load('#card-cvv-container');
      microformRef.current = { microform, number, securityCode };
      setMicroformReady(true);
    } catch (error) {
      console.error('Microform initialization failed', error);
    }
  };
  const openCardCapture = async () => {
    try {
      setLoading(true);
      const response = await postApi(
        '/api/v1/agency/subscription/microform-session',
      );
      const { jwt, client_library } = response.data;
      setMicroformData({ jwt });
      setShowCardModal(true);
      if (!window.Flex) {
        const script = document.createElement('script');
        script.src = client_library;
        script.async = true;
        script.onload = () => initializeMicroform(jwt);
        document.body.appendChild(script);
      } else {
        initializeMicroform(jwt);
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  const handleSetDefault = async (cardId) => {
    let toastMessage = null;
    try {
      const response = await makeDefaultCardAPI(cardId);
      toastMessage =
        response.status == 201
          ? { message: response.message, type: 'success' }
          : { message: response.message, type: 'error' };
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false);
      if (toastMessage) {
        setTimeout(
          () => showToast(toastMessage.message, toastMessage.type),
          300,
        );
      }
    }
  };
  const handleRemoveCard = async (cardId) => {
    let toastMessage = null;
    try {
      const response = await deleteCardAPI(cardId);
      if (response.status == 200) {
        toastMessage = { message: response.message, type: 'success' };
        getCreditCard();
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false);
      if (toastMessage) {
        setTimeout(
          () => showToast(toastMessage.message, toastMessage.type),
          300,
        );
      }
    }
  };
  const plans = [
    {
      title: 'Boutique Agencies',
      subtitle: 'Up to 5 agents',
      price: '$95 / month',
      description:
        'Built for smaller agencies preparing for Tranche 2 AML/CTF compliance.',
      features: [
        'Core AML compliance',
        'Risk assessment & AML program',
        'Training & audit trail',
        'AUSTRAC-aligned reporting',
        '5 included KYC scans',
      ],
      selected: true,
      color: 'emerald',
      action: 'Get Started',
    },
    {
      title: 'Small–Medium Agencies',
      subtitle: '6–25 agents',
      price: '$399 / month',
      description:
        'Designed for agencies needing scalable AML compliance workflows.',
      features: [
        'Core AML compliance',
        'Risk assessment & AML program',
        'Training & audit trail',
        'AUSTRAC-aligned reporting',
        '20 included KYC scans',
      ],
      extras: [
        'Guided onboarding',
        'Full compliance dashboard',
        'Direct customer support',
      ],
      color: 'blue',
      action: 'Contact Us',
    },
    {
      title: 'Growing Agencies',
      subtitle: '26–50 agents',
      price: '$699 / month',
      description:
        'Advanced compliance support for growing agencies managing higher volume.',
      features: [
        'Core AML compliance',
        'Risk assessment & AML program',
        'Training & audit trail',
        'AUSTRAC-aligned reporting',
        '40 included KYC scans',
      ],
      extras: [
        'Advanced analytics',
        'White-glove onboarding',
        'Enterprise-ready workflows',
      ],
      color: 'purple',
      action: 'Contact Us',
    },
    {
      title: 'Enterprise',
      subtitle: '50+ agents',
      price: 'Custom pricing',
      description:
        'Custom compliance solutions for enterprise groups and franchise networks.',
      features: [
        'Core AML compliance',
        'Risk assessment & AML program',
        'Training & audit trail',
        'AUSTRAC-aligned reporting',
        'Custom KYC allowances',
      ],
      extras: [
        'Advanced analytics',
        'White-glove onboarding',
        'Enterprise workflows',
      ],
      color: 'slate',
      action: 'Contact Us',
    },
  ];
  const getCardBrandUI = (brand) => {
    switch (brand) {
      case 'visa':
        return { logo: '/visa.svg', gradient: 'from-blue-500 to-indigo-600' };
      case 'mastercard':
        return {
          logo: '/mastercard.svg',
          gradient: 'from-red-500 to-orange-500',
        };
      case 'amex':
        return { logo: '/amex.svg', gradient: 'from-green-500 to-emerald-600' };
      default:
        return { logo: null, gradient: 'from-gray-600 to-gray-800' };
    }
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const CARDS_PER_PAGE = 1;
  const getCardsPerPage = () => {
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 3;
  };
  const [cardsPerPage, setCardsPerPage] = useState(getCardsPerPage());
  useEffect(() => {
    const handleResize = () => setCardsPerPage(getCardsPerPage());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const totalPages = Math.ceil(cards.length / cardsPerPage);
  const [page, setPage] = useState(0);
  const next = () => setPage((p) => Math.min(p + 1, totalPages - 1));
  const prev = () => setPage((p) => Math.max(p - 1, 0));
  const GetAPIcalls = useCallback(
    async (url, isBlob = false) => {
      const token = localStorage.getItem('jwtToken');
      const apiUrl = `${BASE_URL}${url}`;

      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired.');
        }

        const errorData = await response.json().catch(() => ({
          message: 'Unexpected error',
        }));

        throw new Error(errorData.message);
      }

      return isBlob ? response.blob() : response.json();
    },
    [navigate],
  );
  const getPlanList = async () => {
    try {
      const response = await GetAPIcalls(`/api/v1/subscription`);
      if (response.status == 200) setPlans(response.data.plans);
      else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) showToast(response.message, 'error');
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const getUsage = async () => {
    try {
      const response = await GetAPIcalls(`/api/v1/agency/subscription/usage`);
      if (response.status == 200) setSubscription(response.data);
      else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) showToast(response.message, 'error');
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const getPackageDefaults = async () => {
    try {
      setPackageDefaultsLoading(true);
      const response = await GetAPIcalls(
        `/api/v1/agency/verification-package-defaults`,
      );
      if (response.status == 200) setPackageDefaults(response.data);
      else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) showToast(response.message, 'error');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setPackageDefaultsLoading(false);
    }
  };
  const updatePackageDefaults = async (itemCodes) => {
    const token = localStorage.getItem('jwtToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const apiUrl = `${BASE_URL}/api/v1/agency/verification-package-defaults`;
    try {
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ item_codes: itemCodes }),
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const clearPackageDefaults = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const apiUrl = `${BASE_URL}/api/v1/agency/verification-package-defaults`;
    try {
      const response = await fetch(apiUrl, { method: 'DELETE', headers });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const openEditPackages = () => {
    setDraftItemCodes([...(packageDefaults?.item_codes || [])]);
    setEditPackagesOpen(true);
  };
  const togglePackage = (code) => {
    if (code === 'identity') return;
    setDraftItemCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };
  const handleSavePackages = async () => {
    let toastMessage = null;
    try {
      setSavingPackages(true);
      const response = await updatePackageDefaults(draftItemCodes);
      if (response.status == 200 || response.data) {
        setPackageDefaults(response.data || response);
        toastMessage = {
          message: 'Verification packages updated',
          type: 'success',
        };
        setEditPackagesOpen(false);
      } else {
        toastMessage = {
          message: response.message || 'Update failed',
          type: 'error',
        };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setSavingPackages(false);
      if (toastMessage) {
        setTimeout(
          () => showToast(toastMessage.message, toastMessage.type),
          300,
        );
      }
    }
  };
  const handleResetPackages = async () => {
    let toastMessage = null;
    try {
      setResettingPackages(true);
      const response = await clearPackageDefaults();
      if (response.status == 200 || response.data) {
        setPackageDefaults(response.data || response);
        setDraftItemCodes([]);
        toastMessage = {
          message: 'Reset to platform defaults',
          type: 'success',
        };
        setEditPackagesOpen(false);
      } else {
        toastMessage = {
          message: response.message || 'Reset failed',
          type: 'error',
        };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setResettingPackages(false);
      if (toastMessage) {
        setTimeout(
          () => showToast(toastMessage.message, toastMessage.type),
          300,
        );
      }
    }
  };
  const getPaymentHistory = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/subscription/payments?page=${transactionPage}&limit=10`,
      );
      if (response.status == 200) {
        setTrasansactions(response.data.payments);
        setTotalTransactionPages(response.data.total_pages);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) showToast(response.message, 'error');
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const changeSubAPI = async (planIdToUse = 1) => {
    const token = localStorage.getItem('jwtToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const apiUrl = `${BASE_URL}/api/v1/agency/subscription/upgrade`;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ plan_id: planIdToUse }),
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleChangeSubscription = async () => {
    let toastMessage = null;
    try {
      const planIdToUse =
        (selectedPlan?.id ?? selectedPlan ?? mySub?.plan_id ?? 1) || 1;
      const response = await changeSubAPI(planIdToUse);
      if (response.status == 200) {
        toastMessage = { message: response.message, type: 'success' };
        getMySubscription();
        setOpen(false);
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false);
      if (toastMessage) {
        setTimeout(
          () => showToast(toastMessage.message, toastMessage.type),
          300,
        );
      }
    }
  };
  const cancelSubAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const apiUrl = `${BASE_URL}/api/v1/agency/subscription/cancel`;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleCancelSubscription = async () => {
    let toastMessage = null;
    try {
      const response = await cancelSubAPI();
      if (response.status == 200) {
        toastMessage = {
          message: 'Cancelled successfully',
          type: 'success',
        };
        getMySubscription();
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false);
      if (toastMessage) {
        setTimeout(
          () => showToast(toastMessage.message, toastMessage.type),
          300,
        );
      }
    }
  };

  useEffect(() => {
    if (filter == 'paymentMethod') {
      getPaymentHistory();
      getCreditCard();
    }
    if (filter == 'spend') {
      getSpendDashboard();
      getSpendCustomers();
      getSpendEvents();
      getPlanList();
      getUsage();
      getPackageDefaults();
    }
    if (filter == 'invoices') getInvoices();
  }, [filter]);
  const getMySubscription = async () => {
    try {
      const response = await GetAPIcalls('/api/v1/agency/subscription');
      if (response.status == 200) {
        setMySub(response.data);
        setSelectedPlan(response.data.plan_id);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) showToast(response.message, 'error');
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  useEffect(() => {
    getMySubscription();
  }, []);
  useEffect(() => {
    if (open && mySub?.plan_id != null) setSelectedPlan(mySub.plan_id);
  }, [open, mySub?.plan_id]);

  const capabilityItems = [
    {
      label: 'Risk assessment',
      enabled: mySub?.capabilities?.risk_assessment,
      icon: ShieldCheck,
    },
    {
      label: 'Advanced analytics',
      enabled: mySub?.capabilities?.advanced_analytics,
      icon: TrendingUp,
    },
    {
      label: 'Compliance dashboard',
      enabled: mySub?.capabilities?.compliance_dashboard,
      icon: ShieldCheck,
    },
    {
      label: 'Training & audit trail',
      enabled: mySub?.capabilities?.training_audit_trail,
      icon: CheckCircle2,
    },
  ];

  const PACKAGE_META = {
    identity: {
      name: 'Identity',
      desc: 'Identity verification',
      icon: UserCheck,
      tone: 'from-blue-500/10 to-cyan-500/10 text-blue-700',
    },
    pep: {
      name: 'PEP Screening',
      desc: 'Politically exposed persons',
      icon: ShieldCheck,
      tone: 'from-violet-500/10 to-indigo-500/10 text-violet-700',
    },
    sanctions: {
      name: 'Sanctions Screening',
      desc: 'Sanctions list checks',
      icon: ShieldCheck,
      tone: 'from-emerald-500/10 to-teal-500/10 text-emerald-700',
    },
    media: {
      name: 'Media Monitoring',
      desc: 'Adverse media screening',
      icon: TrendingUp,
      tone: 'from-amber-500/10 to-orange-500/10 text-amber-700',
    },
    watchlist: {
      name: 'Watchlist',
      desc: 'Watchlist screening',
      icon: ScanLine,
      tone: 'from-rose-500/10 to-pink-500/10 text-rose-700',
    },
  };
  const resolvedPackages = (
    packageDefaults?.resolved_packages || ['identity']
  ).map((code) => ({
    code,
    ...(PACKAGE_META[code] || {
      name: code,
      desc: 'Verification package',
      icon: ScanLine,
      tone: 'from-slate-500/10 to-slate-600/10 text-slate-600',
    }),
  }));

  const formatCents = (cents) => {
    if (cents == null || isNaN(cents)) return '$0.00';
    const abs = Math.abs(cents);
    const sign = cents < 0 ? '-' : '';
    return (
      sign +
      '$' +
      (abs / 100).toLocaleString('en-AU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };
  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const [spend, setSpend] = useState(null);
  // Usage by customer — GET /agency/subscription/spend/customers (paginated,
  // biggest spender first, each row carries its package split)
  const SPEND_CUSTOMERS_LIMIT = 10;
  const [spendCustomers, setSpendCustomers] = useState([]);
  const [spendCustomersTotal, setSpendCustomersTotal] = useState(0);
  const [spendCustomersPage, setSpendCustomersPage] = useState(1);
  const [spendCustomersTotalPages, setSpendCustomersTotalPages] = useState(1);
  const [spendCustomersLoading, setSpendCustomersLoading] = useState(false);
  const [spendEvents, setSpendEvents] = useState([]);
  const [spendLoading, setSpendLoading] = useState(false);
  // Spend period filter (SpendCalendarFilter, controlled mode) — same
  // options as the invoices filter:
  //   { type: 'month', year, month } → ?year=YYYY&month=M (single Sydney month)
  //   { type: 'last-month' }         → previous Sydney calendar month (year+month)
  //   { type: 'last-3-months' }      → ?months=3 (anchored on current Sydney month)
  //   { type: 'all-time' }           → ?all_time=true (first usage → now)
  const [spendFilter, setSpendFilter] = useState(() => {
    const now = new Date();
    return {
      type: 'month',
      year: now.getFullYear(),
      month: now.getMonth() + 1,
    };
  });
  const [agentFilter, setAgentFilter] = useState('');
  const [usageOpen, setUsageOpen] = useState({
    packages: true,
    breakdown: false,
    customers: false,
    cancel: false,
  });
  const toggleUsage = (key) => setUsageOpen((s) => ({ ...s, [key]: !s[key] }));

  // Period change for the "My Usage" tab (SpendCalendarFilter, controlled mode)
  const handleSpendFilterChange = (nextFilter) => {
    setSpendFilter(nextFilter);
    setSpendCustomersPage(1);
  };
  const spendQuery = (extra) => {
    const params = new URLSearchParams();
    const f = spendFilter;
    if (f?.type === 'all-time') {
      params.append('all_time', 'true');
    } else if (f?.type === 'last-3-months') {
      params.append('months', '3');
    } else if (f?.type === 'last-month') {
      const now = new Date();
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      params.append('year', String(prev.getFullYear()));
      params.append('month', String(prev.getMonth() + 1));
    } else if (f?.type === 'month' && f.year && f.month) {
      params.append('year', String(f.year));
      params.append('month', String(f.month));
    }
    if (agentFilter) params.append('agent_id', agentFilter);
    const qs = params.toString();
    return extra ? `?${qs}&${extra}` : `?${qs}`;
  };
  const getSpendDashboard = async () => {
    try {
      setSpendLoading(true);
      const response = await GetAPIcalls(
        `/api/v1/agency/subscription/spend${spendQuery()}`,
      );
      if (response.status == 200) setSpend(response.data);
      else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          showToast(response.message || 'Spend dashboard unavailable', 'error');
          setSpend(null);
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSpendLoading(false);
    }
  };
  // GET /agency/subscription/spend/customers?year=&month=&limit=&page=
  // → { customers: [{ customer_user_id, customer_name, client_id,
  //     verification_count, usage_cents, packages: [...] }],
  //     page, limit, total, total_pages }
  const getSpendCustomers = async () => {
    try {
      setSpendCustomersLoading(true);
      const response = await GetAPIcalls(
        `/api/v1/agency/subscription/spend/customers${spendQuery(
          `page=${spendCustomersPage}&limit=${SPEND_CUSTOMERS_LIMIT}`,
        )}`,
      );
      if (response.status == 200) {
        setSpendCustomers(response.data?.customers || []);
        setSpendCustomersTotal(response.data?.total ?? 0);
        setSpendCustomersTotalPages(response.data?.total_pages ?? 1);
      } else {
        setSpendCustomers([]);
        setSpendCustomersTotal(0);
        setSpendCustomersTotalPages(1);
      }
    } catch (err) {
      setSpendCustomers([]);
      setSpendCustomersTotal(0);
      setSpendCustomersTotalPages(1);
      showToast(err.message, 'error');
    } finally {
      setSpendCustomersLoading(false);
    }
  };
  const getSpendEvents = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/subscription/spend/events${spendQuery()}`,
      );
      if (response.status == 200) {
        setSpendEvents(
          Array.isArray(response.data)
            ? response.data
            : response.data?.events || [],
        );
      }
    } catch (err) {
      setSpendEvents([]);
    }
  };
  useEffect(() => {
    console.log('spending', spendFilter);
    if (filter === 'spend') {
      getSpendDashboard();
      getSpendEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spendFilter, agentFilter]);
  useEffect(() => {
    if (filter === 'spend') getSpendCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spendCustomersPage, spendFilter, agentFilter]);

  const [invoices, setInvoices] = useState([]);
  const [invoicesTotal, setInvoicesTotal] = useState(0);
  const [invoicePage, setInvoicePage] = useState(1);
  const [totalInvoicePages, setTotalInvoicePages] = useState(1);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  // Invoices period filter (SpendCalendarFilter, controlled mode):
  //   { type: 'month', year, month } → ?year=YYYY&month=M (single Sydney month)
  //   { type: 'last-month' }         → previous Sydney calendar month (year+month)
  //   { type: 'last-3-months' }      → ?months=3 (anchored on current Sydney month)
  //   { type: 'all-time' }           → ?all_time=true (first usage → now)
  //   nothing else                   → no date params (all invoices)
  const [invoiceFilter, setInvoiceFilter] = useState(() => {
    const now = new Date();
    return {
      type: 'month',
      year: now.getFullYear(),
      month: now.getMonth() + 1,
    };
  });

  const handleInvoiceFilterChange = (nextFilter) => {
    setInvoiceFilter(nextFilter);
    setInvoicePage(1);
  };
  const buildInvoicesQuery = () => {
    const params = new URLSearchParams();
    params.append('page', String(invoicePage));
    params.append('limit', '10');
    const f = invoiceFilter;
    if (f?.type === 'all-time') {
      params.append('all_time', 'true');
    } else if (f?.type === 'last-3-months') {
      params.append('months', '3');
    } else if (f?.type === 'last-month') {
      const now = new Date();
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      params.append('year', String(prev.getFullYear()));
      params.append('month', String(prev.getMonth() + 1));
    } else if (f?.type === 'month' && f.year && f.month) {
      params.append('year', String(f.year));
      params.append('month', String(f.month));
    }
    return `?${params.toString()}`;
  };
  const getInvoices = async () => {
    try {
      setInvoiceLoading(true);
      const response = await GetAPIcalls(
        `/api/v1/agency/subscription/invoices${buildInvoicesQuery()}`,
      );
      if (response.status == 200) {
        setInvoices(response.data.invoices || []);
        setInvoicesTotal(response.data.total ?? 0);
        setTotalInvoicePages(response.data.total_pages ?? 1);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setInvoiceLoading(false);
    }
  };
  useEffect(() => {
    if (filter === 'invoices') getInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicePage, invoiceFilter]);
  const openInvoiceDetail = async (invoiceId) => {
    try {
      setDetailLoading(true);
      const response = await GetAPIcalls(
        `/api/v1/agency/subscription/invoices/${invoiceId}`,
      );
      if (response.status == 200) {
        setInvoiceDetail(response.data);
        localStorage.setItem('billingFilter', 'invoices');
        navigate(`/invoices/${invoiceId}`, {
          state: {
            invoice: response.data,
            from: location.pathname,
            historyStack: [
              ...(location.state?.historyStack || []),
              location.pathname,
            ],
          },
        });
      } else {
        showToast(response.message || 'Could not load invoice', 'error');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDetailLoading(false);
    }
  };
  const downloadInvoicePdf = async (invoiceId) => {
    try {
      const blob = await GetAPIcalls(
        `/api/v1/agency/subscription/invoices/${invoiceId}/pdf`,
        true,
      );
      if (!blob || blob.size === 0) {
        showToast('No PDF available for this invoice', 'error');
        return;
      }
      const invoiceNumber =
        (invoiceDetail?.id === invoiceId && invoiceDetail.invoice_number) ||
        invoices.find((inv) => inv.id === invoiceId)?.invoice_number;
      const pdfBlob =
        blob.type === 'application/pdf'
          ? blob
          : new Blob([blob], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${invoiceNumber || `invoice-${invoiceId}`}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const invoiceStatusUI = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'paid':
        return 'bg-emerald-100 text-emerald-700';
      case 'issued':
        return 'bg-blue-100 text-blue-700';
      case 'draft':
        return 'bg-slate-100 text-slate-600';
      case 'failed':
        return 'bg-red-100 text-red-700';
      case 'void':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };
  const spendKpis = spend
    ? [
        {
          label: 'Gross usage',
          value: formatCents(spend.usage_cents),
          sub: `${spend.verification_count ?? 0} verifications`,
          icon: TrendingUp,
          tone: 'from-blue-500/10 to-cyan-500/10 text-blue-700',
        },
        {
          label: 'Credit applied',
          value: formatCents(spend.credit_applied_cents),
          sub: `${formatCents(spend.credit_cents)} allowance`,
          icon: Sparkles,
          tone: 'from-emerald-500/10 to-teal-500/10 text-emerald-700',
        },
        {
          label: 'Charged (incl GST)',
          value: formatCents(spend.total_cents),
          sub: `${spend.tax_cents != null ? formatCents(spend.tax_cents) : ''} GST`,
          icon: Wallet,
          tone: 'from-violet-500/10 to-indigo-500/10 text-violet-700',
        },
      ]
    : [];

  return (
    <div className=" lg:min-w-[78vw] lg:max-w-[80vw] ">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div className="flex justify-left">
        <div className="bg-white p-1 rounded-xl flex gap-1 w-full md:w-fit">
          {[
            { key: 'spend', label: 'My Usage', icon: 'monitoring' },
            { key: 'invoices', label: 'Invoices', icon: 'receipt_long' },
            {
              key: 'paymentMethod',
              label: 'Payment Details',
              icon: 'credit_card',
            },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setSelectedCard(null);
                setFilter(tab.key);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-mormal transition-all ${
                filter === tab.key
                  ? 'bg-[#375AE7] shadow text-white'
                  : 'text-black hover:text-black'
              }`}
            >
              <span className="material-symbols-outlined text-sm">
                {tab.icon}
              </span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {filter === 'paymentMethod' && (
        <div className="lg:min-w-[78vw] lg:max-w-[80vw]">
          <div className="w-full p-4 bg-white rounded-lg mt-4 shadow-lg space-y-6">
            <div className="flex justify-end mb-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openCardCapture();
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-[#375AE7] text-white rounded-xl shadow hover:bg-[#2f4fd1] transition"
              >
                <CreditCard size={16} />
                Add New Card
              </button>
            </div>
            <div className="relative mt-6 overflow-hidden px-1 pb-2">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${page * 100}%)` }}
              >
                {Array.from({ length: totalPages }).map((_, pageIndex) => {
                  const start = pageIndex * cardsPerPage;
                  const pageCards = cards.slice(start, start + cardsPerPage);
                  return (
                    <div
                      key={pageIndex}
                      className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 px-1 shrink-0"
                    >
                      {pageCards.map((card) => {
                        const brandUI = getCardBrandUI(
                          card.card_brand?.toLowerCase(),
                        );
                        return (
                          <div
                            key={card.id}
                            className="relative rounded-3xl border transition-all duration-300 border-slate-200 bg-white"
                          >
                            <div
                              onClick={() => setSelectedCard(card)}
                              className="relative h-[210px] p-5 rounded-3xl cursor-pointer overflow-hidden bg-gradient-to-br from-white via-slate-50 to-blue-50"
                            >
                              <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-blue-200/30 blur-3xl" />
                              <div className="relative flex justify-between items-start">
                                <p className="text-[11px] tracking-widest uppercase text-slate-400 font-semibold">
                                  {card.card_brand}
                                </p>
                                <div className="flex items-center gap-2">
                                  {card.is_default && (
                                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-semibold">
                                      <CheckCircle size={12} />
                                      Default
                                    </div>
                                  )}
                                  {brandUI.logo && (
                                    <img
                                      src={brandUI.logo}
                                      className="h-8 object-contain"
                                      alt=""
                                    />
                                  )}
                                </div>
                              </div>
                              <div className="mt-7 h-9 w-14 rounded-lg bg-gradient-to-br from-yellow-200 to-yellow-400" />
                              <p className="mt-5 mb-4 text-xl tracking-[0.22em] font-semibold text-slate-800">
                                •••• •••• •••• {card.card_last4}
                              </p>
                              <div className="absolute bottom-5 left-5 right-5 flex justify-between mt-2">
                                <div className="mt-2">
                                  <p className="text-[11px] mt-2 text-slate-400">
                                    Expires
                                  </p>
                                  <p className="text-sm font-semibold text-slate-700">
                                    {String(card.card_expiry_month).padStart(
                                      2,
                                      '0',
                                    )}
                                    /{String(card.card_expiry_year).slice(-2)}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="p-4 flex gap-3 border-slate-100">
                              {!card.is_default && (
                                <button
                                  onClick={() => {
                                    setLoading(true);
                                    handleSetDefault(card.id);
                                  }}
                                  className="flex-1 h-10 rounded-xl bg-[#375AE7] text-white text-sm font-semibold hover:bg-[#2f4fd1] transition"
                                >
                                  Make Default
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setLoading(true);
                                  handleRemoveCard(card.id);
                                }}
                                className="flex-1 h-10 rounded-xl border border-red-200 bg-red-50 text-red-600 text-sm font-semibold hover:bg-red-100 transition"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center gap-3 mt-5">
                <button
                  onClick={prev}
                  disabled={page === 0}
                  className="px-3 py-2 rounded-full border bg-white shadow-sm disabled:opacity-30"
                >
                  <ChevronLeft size={18} />
                </button>
                <div className="flex items-center gap-2">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        i === page ? 'bg-[#375AE7] scale-125' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={next}
                  disabled={page === totalPages - 1}
                  className="px-3 py-2 rounded-full border bg-white shadow-sm disabled:opacity-30"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
              {transactions.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                        <Calendar size={20} className="text-[#375AE7]" />
                        Payment Transactions
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        View your subscription payment activity
                      </p>
                    </div>
                    <div className="bg-blue-50 text-[#375AE7] px-3 py-1 rounded-full text-sm font-medium">
                      {transactions.length} Records
                    </div>
                  </div>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-slate-500 border-b">
                          <th className="pb-4">Transaction</th>
                          <th className="pb-4">Amount</th>
                          <th className="pb-4">Status</th>
                          <th className="pb-4">Date</th>
                          <th className="pb-4">Settlement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((txn) => (
                          <tr
                            key={txn.id}
                            className="border-b last:border-none hover:bg-slate-50 transition"
                          >
                            <td className="py-5">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                                  <ArrowLeftRight
                                    size={18}
                                    className="text-[#375AE7]"
                                  />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-800">
                                    {txn.transaction_type}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    ID: #{txn.id}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="py-5">
                              <p className="font-semibold text-slate-900">
                                {txn.currency} {Number(txn.amount).toFixed(2)}
                              </p>
                              {txn.auth_code && (
                                <p className="text-xs text-slate-500">
                                  Auth: {txn.auth_code}
                                </p>
                              )}
                            </td>
                            <td className="py-5">
                              <span
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                                  txn.status === 'SETTLED'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : txn.status === 'AUTHORIZED'
                                      ? 'bg-blue-100 text-blue-700'
                                      : txn.status === 'REFUNDED'
                                        ? 'bg-purple-100 text-purple-700'
                                        : txn.status === 'VOIDED' ||
                                            txn.status === 'REVERSED'
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {txn.status}
                              </span>
                            </td>
                            <td className="py-5 text-sm text-slate-600">
                              {txn.created_at
                                ? new Date(txn.created_at).toLocaleDateString(
                                    'en-AU',
                                    {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                    },
                                  )
                                : '-'}
                            </td>
                            <td className="py-5 text-sm text-slate-600">
                              {txn.settled_at ? (
                                new Date(txn.settled_at).toLocaleDateString(
                                  'en-AU',
                                  {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                  },
                                )
                              ) : (
                                <span className="text-slate-400">
                                  Not settled
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="md:hidden space-y-4">
                    {transactions.map((txn) => (
                      <div
                        key={txn.id}
                        className="border border-slate-200 rounded-2xl p-4 hover:shadow-md transition"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                              <ArrowLeftRight
                                size={18}
                                className="text-[#375AE7]"
                              />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">
                                {txn.transaction_type}
                              </p>
                              <p className="text-xs text-slate-500">
                                #{txn.id}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                              txn.status === 'SETTLED'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {txn.status}
                          </span>
                        </div>
                        <div className="mt-4 flex justify-between">
                          <div>
                            <p className="text-xs text-slate-500">Amount</p>
                            <p className="font-bold text-lg">
                              {txn.currency} {txn.amount}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Date</p>
                            <p className="text-sm">
                              {txn.created_at
                                ? new Date(txn.created_at).toLocaleDateString(
                                    'en-AU',
                                  )
                                : '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
                    <button
                      disabled={transactionPage === 1}
                      onClick={() => setTransactionPage((p) => p - 1)}
                      className="px-4 py-2 rounded-xl border text-sm disabled:opacity-40 hover:bg-slate-50"
                    >
                      Previous
                    </button>
                    {[...Array(totalTransactionPages)].map((_, idx) => {
                      const pg = idx + 1;
                      return (
                        <button
                          key={pg}
                          onClick={() => setTransactionPage(pg)}
                          className={`w-10 h-10 rounded-xl text-sm font-medium ${
                            pg === transactionPage
                              ? 'bg-[#375AE7] text-white shadow'
                              : 'border hover:bg-slate-50'
                          }`}
                        >
                          {pg}
                        </button>
                      );
                    })}
                    <button
                      disabled={transactionPage === totalTransactionPages}
                      onClick={() => setTransactionPage((p) => p + 1)}
                      className="px-4 py-2 rounded-xl border text-sm disabled:opacity-40 hover:bg-slate-50"
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-5">
                    <ArrowLeftRight size={26} className="text-[#375AE7]" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-800">
                    No Payment History
                  </h4>
                  <p className="text-sm text-slate-500 mt-2 text-center max-w-md">
                    Your subscription payments and transaction history will
                    appear here.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== MY USAGE — collapsible ===== */}
      {filter === 'spend' && (
        <div className="lg:min-w-[78vw] lg:max-w-[80vw]">
          <div className="w-full p-3 sm:p-4 bg-white rounded-lg mt-4 shadow-lg space-y-3 sm:space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#375AE7] text-xl sm:text-2xl">
                    monitoring
                  </span>
                  Usage Details
                </h1>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  Verification usage, and credits.
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 self-end sm:self-auto">
                <SpendCalendarFilter
                  spend={spend}
                  value={spendFilter}
                  onChange={handleSpendFilterChange}
                />
              </div>
            </div>

            {spend && !spendLoading && (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3">
                {spendKpis.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={index}
                      className={`rounded-2xl border border-slate-200 bg-gradient-to-br ${item.tone} p-3 sm:p-4`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/70">
                          <Icon size={14} />
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-right leading-tight">
                          {item.label}
                        </span>
                      </div>
                      <p className="mt-2 text-base sm:text-xl font-bold text-slate-900 truncate">
                        {item.value}
                      </p>
                      <p className="mt-0.5 text-[11px] sm:text-xs text-slate-500 truncate">
                        {item.sub}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            <UsageAccordion
              open={usageOpen.packages}
              onToggle={() => toggleUsage('packages')}
              icon={ShieldCheck}
              iconWrapClass="bg-gradient-to-br from-[#375AE7] via-[#4F6CFF] to-[#2E5BFF] text-white shadow-lg shadow-blue-200"
              title="Default verification package"
              subtitle="Included features — edit or add more anytime"
              badge={
                <span className="rounded-full bg-blue-50 text-[#375AE7] px-2 py-0.5 text-[10px] font-semibold">
                  {resolvedPackages.length} included
                </span>
              }
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                  Included verifications
                </p>
                <button
                  onClick={openEditPackages}
                  disabled={packageDefaultsLoading && !packageDefaults}
                  className="inline-flex items-center gap-1 rounded-full bg-[#375AE7] px-4 py-1 text-sm font-semibold text-white hover:bg-[#2f4fd1] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusCircle size={15} />
                  Edit
                </button>
              </div>
              {packageDefaultsLoading && !packageDefaults ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
                  <Loader2 size={16} className="animate-spin text-[#375AE7]" />
                  Loading packages…
                </div>
              ) : (
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                  {resolvedPackages.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={index}
                        className={`rounded-2xl border border-slate-200 bg-gradient-to-br ${item.tone} p-3`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/70">
                            <Icon size={16} />
                          </div>
                          <p className="text-sm font-semibold text-slate-700">
                            {item.name}
                          </p>
                        </div>
                        <p className="mt-2 text-sm text-slate-500">
                          {item.desc}
                        </p>
                        {item.code === 'identity' && (
                          <span className="mt-2 inline-block rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                            Always included
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </UsageAccordion>

            {spendLoading && !spend ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 size={32} className="animate-spin text-[#375AE7]" />
                <p className="text-sm text-slate-500 mt-4">
                  Loading spend dashboard…
                </p>
              </div>
            ) : !spend ? (
              <div className="flex flex-col items-center justify-center py-12 border border-dashed border-slate-200 rounded-3xl">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                  <PieChart size={24} className="text-[#375AE7]" />
                </div>
                <h4 className="text-base font-semibold text-slate-800">
                  No spend data for this period
                </h4>
                <p className="text-sm text-slate-500 mt-2 text-center max-w-md px-4">
                  The spend dashboard is only available for volume
                  subscriptions. Switch the month or contact support for
                  assistance.
                </p>
              </div>
            ) : (
              <>
                <UsageAccordion
                  open={usageOpen.breakdown}
                  onToggle={() => toggleUsage('breakdown')}
                  icon={PieChart}
                  iconWrapClass="bg-gradient-to-br from-[#375AE7]/15 to-[#4F6CFF]/15 text-[#375AE7]"
                  title="Usage breakdown"
                  subtitle="Package spend and GST"
                  badge={
                    spend.packages?.length ? (
                      <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold">
                        {spend.packages.length} packages
                      </span>
                    ) : null
                  }
                >
                  <UsageBreakdownPanel
                    spend={spend}
                    formatCents={formatCents}
                  />
                </UsageAccordion>

                <UsageAccordion
                  open={usageOpen.customers}
                  onToggle={() => toggleUsage('customers')}
                  icon={UsersRound}
                  iconWrapClass="bg-gradient-to-br from-violet-500/15 to-indigo-500/15 text-violet-600"
                  title="Usage by customer"
                  subtitle="How much was spent on each customer, biggest spender first"
                  badge={
                    spendCustomersTotal ? (
                      <span className="rounded-full bg-violet-50 text-violet-700 px-2 py-0.5 text-[10px] font-semibold">
                        {spendCustomersTotal}{' '}
                        {spendCustomersTotal === 1 ? 'customer' : 'customers'}
                      </span>
                    ) : null
                  }
                >
                  <UsageByCustomerPanel
                    customers={spendCustomers}
                    total={spendCustomersTotal}
                    page={spendCustomersPage}
                    totalPages={spendCustomersTotalPages}
                    limit={SPEND_CUSTOMERS_LIMIT}
                    loading={spendCustomersLoading}
                    onPageChange={setSpendCustomersPage}
                    totalUsageCents={spend.usage_cents}
                    formatCents={formatCents}
                  />
                </UsageAccordion>
              </>
            )}

            <UsageAccordion
              open={usageOpen.cancel}
              onToggle={() => toggleUsage('cancel')}
              icon={X}
              iconWrapClass="bg-red-100 text-red-600"
              variant="danger"
              title="Cancel"
              subtitle="No notice period or exit fees, takes effect at period end"
            >
              <p className="text-sm text-slate-600 leading-relaxed">
                You can cancel anytime. Your account will remain active until
                the end of your current billing period, there is no notice
                period and no exit fees. Certificates you have issued stay
                verifiable, and your records remain retrievable for the
                statutory seven-year retention period.
              </p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-white border border-slate-100 rounded-2xl p-3 sm:p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="material-symbols-outlined text-orange-500 text-lg">
                      calendar_month
                    </span>
                    <h4 className="font-semibold text-slate-800 text-sm">
                      Takes Effect
                    </h4>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    At the end of the current billing period. No notice period
                    or exit fees.
                  </p>
                </div>
                <div className="bg-white border border-slate-100 rounded-2xl p-3 sm:p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="material-symbols-outlined text-blue-500 text-lg">
                      verified
                    </span>
                    <h4 className="font-semibold text-slate-800 text-sm">
                      Certificates
                    </h4>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Certificates you have issued remain verifiable after
                    cancellation.
                  </p>
                </div>
                <div className="bg-white border border-slate-100 rounded-2xl p-3 sm:p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="material-symbols-outlined text-purple-500 text-lg">
                      folder_copy
                    </span>
                    <h4 className="font-semibold text-slate-800 text-sm">
                      Record Retention
                    </h4>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Your records remain retrievable for the statutory seven-year
                    retention period.
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setLoading(true);
                  handleCancelSubscription();
                }}
                className="group mt-4 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-red-700 text-white font-semibold text-sm shadow-md hover:bg-red-600 hover:shadow-lg transition-all duration-200 w-full sm:w-auto"
              >
                <X size={16} className="group-hover:rotate-90 transition" />
                Cancel Now
              </button>
            </UsageAccordion>
          </div>
        </div>
      )}

      {filter === 'invoices' && (
        <div className="lg:min-w-[78vw] lg:max-w-[80vw]">
          <div className="w-full p-4 bg-white rounded-lg mt-4 shadow-lg space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#375AE7]">
                    receipt_long
                  </span>
                  Invoices
                </h1>
                <p className="text-sm text-slate-600 mt-2">
                  Review your issued invoices, line items and download tax
                  invoices.
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 w-full md:w-auto self-end md:self-auto">
                <SpendCalendarFilter
                  spend={spend}
                  value={invoiceFilter}
                  onChange={handleInvoiceFilterChange}
                />
                <div className="bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm text-sm text-slate-600 whitespace-nowrap shrink-0">
                  <span className="font-bold text-slate-900">
                    {invoicesTotal}
                  </span>{' '}
                  invoices
                </div>
              </div>
            </div>
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_10px_40px_-20px_rgba(15,23,42,0.35)] sm:p-6">
              {invoiceLoading && invoices.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 size={32} className="animate-spin text-[#375AE7]" />
                  <p className="text-sm text-slate-500 mt-4">
                    Loading invoices…
                  </p>
                </div>
              ) : invoices.length > 0 ? (
                <>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-slate-500 border-b">
                          <th className="pb-4">Invoice</th>
                          <th className="pb-4">Period</th>
                          <th className="pb-4">Amount</th>
                          <th className="pb-4">Status</th>
                          <th className="pb-4">Issued</th>
                          <th className="pb-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv) => (
                          <tr
                            key={inv.id}
                            className="border-b last:border-none hover:bg-slate-50 transition cursor-pointer"
                            onClick={() => openInvoiceDetail(inv.id)}
                          >
                            <td className="py-5">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                                  <ReceiptText
                                    size={18}
                                    className="text-[#375AE7]"
                                  />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-800">
                                    {inv.invoice_number}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    #{inv.id}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="py-5 text-sm text-slate-600">
                              {formatDate(inv.period_start)} –{' '}
                              {formatDate(inv.period_end)}
                            </td>
                            <td className="py-5">
                              <p className="font-semibold text-slate-900">
                                {formatCents(inv.total_cents)}
                              </p>
                              <p className="text-xs text-slate-500">
                                {inv.subtotal_cents != null
                                  ? formatCents(inv.subtotal_cents)
                                  : ''}{' '}
                                + {inv.tax_label || 'GST'}{' '}
                                {formatCents(inv.tax_amount_cents)}
                              </p>
                            </td>
                            <td className="py-5">
                              <span
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${invoiceStatusUI(
                                  inv.status,
                                )}`}
                              >
                                {inv.status}
                              </span>
                            </td>
                            <td className="py-5 text-sm text-slate-600">
                              {formatDate(inv.issued_at)}
                            </td>
                            <td className="py-5">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openInvoiceDetail(inv.id);
                                  }}
                                  className="px-3 py-2 rounded-xl bg-[#375AE7] text-white text-xs font-semibold hover:bg-[#2f4fd1] transition"
                                >
                                  Details
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadInvoicePdf(inv.id);
                                  }}
                                  disabled={!inv.pdf_available}
                                  title={
                                    inv.pdf_available
                                      ? 'Download PDF'
                                      : 'PDF not available'
                                  }
                                  className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <FileDown size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="md:hidden space-y-4">
                    {invoices.map((inv) => (
                      <div
                        key={inv.id}
                        onClick={() => openInvoiceDetail(inv.id)}
                        className="border border-slate-200 rounded-2xl p-4 hover:shadow-md transition cursor-pointer"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                              <ReceiptText
                                size={18}
                                className="text-[#375AE7]"
                              />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">
                                {inv.invoice_number}
                              </p>
                              <p className="text-xs text-slate-500">
                                {formatDate(inv.period_start)} –{' '}
                                {formatDate(inv.period_end)}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${invoiceStatusUI(
                              inv.status,
                            )}`}
                          >
                            {inv.status}
                          </span>
                        </div>
                        <div className="mt-4 flex justify-between items-center">
                          <div>
                            <p className="text-xs text-slate-500">Total</p>
                            <p className="font-bold text-lg">
                              {formatCents(inv.total_cents)}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadInvoicePdf(inv.id);
                            }}
                            disabled={!inv.pdf_available}
                            className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
                          >
                            <FileDown size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
                    <button
                      disabled={invoicePage === 1}
                      onClick={() => setInvoicePage((p) => p - 1)}
                      className="px-4 py-2 rounded-xl border text-sm disabled:opacity-40 hover:bg-slate-50"
                    >
                      Previous
                    </button>
                    {[...Array(totalInvoicePages)].map((_, idx) => {
                      const pg = idx + 1;
                      return (
                        <button
                          key={pg}
                          onClick={() => setInvoicePage(pg)}
                          className={`w-10 h-10 rounded-xl text-sm font-medium ${
                            pg === invoicePage
                              ? 'bg-[#375AE7] text-white shadow'
                              : 'border hover:bg-slate-50'
                          }`}
                        >
                          {pg}
                        </button>
                      );
                    })}
                    <button
                      disabled={invoicePage === totalInvoicePages}
                      onClick={() => setInvoicePage((p) => p + 1)}
                      className="px-4 py-2 rounded-xl border text-sm disabled:opacity-40 hover:bg-slate-50"
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-5">
                    <ReceiptText size={26} className="text-[#375AE7]" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-800">
                    No invoices yet
                  </h4>
                  <p className="text-sm text-slate-500 mt-2 text-center max-w-md">
                    Your issued tax invoices will appear here once billing
                    begins.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showCardModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-50 px-4">
          <div className="w-full max-w-xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-[#375AE7] to-indigo-600 p-6 text-white">
              <div className="flex justify-between items-start">
                <div className="flex gap-4 items-center">
                  <div className="h-14 w-14 rounded-2xl bg-white/20 flex items-center justify-center">
                    <CreditCard size={28} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Add Payment Card</h2>
                    <p className="text-sm text-blue-100 mt-1">
                      Secure payment powered by NAB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowCardModal(false);
                    clearCardForm();
                  }}
                  className="h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
              <div className="space-y-5">
                <div className="relative overflow-hidden flex items-start gap-4 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 border border-emerald-200 rounded-3xl p-5 shadow-sm">
                  <div className="absolute right-0 top-0 h-20 w-20 bg-emerald-200/30 rounded-full blur-2xl" />
                  <div className="relative h-12 w-12 rounded-2xl bg-white shadow-md flex items-center justify-center shrink-0">
                    <LockKeyhole size={24} className="text-emerald-600" />
                  </div>
                  <div className="relative">
                    <p className="font-semibold text-emerald-900 text-sm">
                      Secure payment encryption enabled
                    </p>
                    <p className="text-xs text-emerald-700 mt-1 leading-relaxed">
                      Your card details are securely processed through NAB and
                      are never stored on our systems.
                    </p>
                  </div>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Info size={19} className="text-[#375AE7]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-800">
                        Supported payment cards
                      </h3>
                      <p className="text-xs text-slate-500">
                        Securely add one of the supported cards below
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed mb-5">
                    We currently accept only Visa, Mastercard and American
                    Express cards. Other card types are not supported.
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {['visa', 'mastercard', 'amex'].map((brand) => {
                      const card = getCardBrandUI(brand);
                      return (
                        <div
                          key={brand}
                          className="group relative h-20 rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                        >
                          <div
                            className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${card.gradient}`}
                          />
                          <div className="h-full flex flex-col items-center justify-center gap-2">
                            {card.logo && (
                              <img
                                src={card.logo}
                                alt={`${brand} card`}
                                className="h-8 w-auto object-contain transition group-hover:scale-110"
                              />
                            )}
                            <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">
                              Accepted
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="text-sm font-semibold text-slate-700 mb-2 block">
                    Card Number
                  </label>
                  <div
                    className={`relative h-[54px] rounded-xl px-4 flex items-center transition-all border ${
                      cardError
                        ? 'border-red-400 ring-1 ring-red-100'
                        : cardFocused
                          ? 'ring-2 ring-[#375AE7]'
                          : 'border-slate-200'
                    } bg-white shadow-sm`}
                  >
                    <CreditCard size={18} className="text-slate-400 mr-2" />
                    <div
                      id="card-number-container"
                      className="flex-1 h-full flex items-center"
                    />
                    {cardSuccess && <SuccessTick />}
                  </div>
                  {cardError && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      ⚠ {cardError}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-2 block">
                    Expiry
                  </label>
                  <div className="relative">
                    <Calendar
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 mr-2"
                    />
                    <input
                      value={cardExpiry}
                      onChange={(e) =>
                        setCardExpiry(formatExpiry(e.target.value))
                      }
                      placeholder="MM/YY"
                      maxLength={5}
                      className="w-full rounded-xl border border-slate-200 pl-10 py-3 focus:ring-2 focus:ring-[#375AE7] outline-none placeholder:text-[#b6c2d1]"
                    />
                    {!expiryError && cardExpiry.length === 5 && (
                      <div className="absolute right-3 top-3">
                        <SuccessTick />
                      </div>
                    )}
                  </div>
                  {expiryError && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      ⚠ {expiryError}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    CVV
                    <span className="text-xs text-slate-400">(3–4 digits)</span>
                  </label>
                  <div
                    className={`relative h-[54px] rounded-xl px-3 flex items-center transition-all border ${
                      cvvError
                        ? 'border-red-400 ring-1 ring-red-100'
                        : cvvFocused
                          ? 'ring-2 ring-[#375AE7]'
                          : 'border-slate-200'
                    } bg-white shadow-sm`}
                  >
                    <ShieldCheck size={18} className="text-slate-400 mr-2" />
                    <div
                      id="card-cvv-container"
                      className="flex-1 h-full flex items-center"
                    />
                    {cvvSuccess && <SuccessTick />}
                  </div>
                  {cvvError && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      ⚠ {cvvError}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-blue-100 bg-blue-50 p-4">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    Default payment method
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Use this card automatically for future payments
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isDefaultCard}
                    onChange={() => setIsDefaultCard(!isDefaultCard)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-300 rounded-full peer peer-checked:bg-[#375AE7] transition after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:bg-white after:rounded-full after:transition peer-checked:after:translate-x-full" />
                </label>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">
                      Billing Address
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Use a different billing address for this card
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isBillingDifferent}
                      onChange={() =>
                        setIsBillingDifferent(!isBillingDifferent)
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 rounded-full peer peer-checked:bg-[#375AE7] transition after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-all peer-checked:after:translate-x-full" />
                  </label>
                </div>
              </div>
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  isBillingDifferent
                    ? 'max-h-[500px] opacity-100 mt-4'
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-slate-200 rounded-2xl p-4 bg-white">
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-600 mb-1 block">
                      Address
                    </label>
                    <input
                      type="text"
                      value={billingAddress.address}
                      onChange={(e) =>
                        setBillingAddress({
                          ...billingAddress,
                          address: e.target.value,
                        })
                      }
                      placeholder="Street address"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
                    />
                    {billingErrors.address && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠ {billingErrors.address}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-1 block">
                      City
                    </label>
                    <input
                      type="text"
                      value={billingAddress.city}
                      onChange={(e) =>
                        setBillingAddress({
                          ...billingAddress,
                          city: e.target.value,
                        })
                      }
                      placeholder="City"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
                    />
                    {billingErrors.city && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠ {billingErrors.city}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-1 block">
                      State
                    </label>
                    <input
                      type="text"
                      value={billingAddress.state}
                      onChange={(e) =>
                        setBillingAddress({
                          ...billingAddress,
                          state: e.target.value,
                        })
                      }
                      placeholder="State"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
                    />
                    {billingErrors.state && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠ {billingErrors.state}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-1 block">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      value={billingAddress.zip}
                      onChange={(e) =>
                        setBillingAddress({
                          ...billingAddress,
                          zip: e.target.value,
                        })
                      }
                      placeholder="ZIP Code"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
                    />
                    {billingErrors.zip && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠ {billingErrors.zip}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-1 block">
                      Country
                    </label>
                    <input
                      type="text"
                      value={billingAddress.country}
                      onChange={(e) =>
                        setBillingAddress({
                          ...billingAddress,
                          country: e.target.value,
                        })
                      }
                      placeholder="Country"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
                    />
                    {billingErrors.country && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠ {billingErrors.country}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-200 p-5 flex flex-col sm:flex-row gap-3 justify-end bg-white sticky bottom-0">
              <button
                onClick={() => {
                  setShowCardModal(false);
                  clearCardForm();
                }}
                className="h-12 px-6 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setSaveAttempted(true);
                  if (validateCardForm()) {
                    setLoading(true);
                    handleSaveCard();
                  }
                }}
                disabled={
                  !microformReady ||
                  savingCard ||
                  !cardSuccess ||
                  !cvvSuccess ||
                  !cardExpiry
                }
                className={`h-12 px-8 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                  !cardSuccess || !cvvSuccess || !cardExpiry || savingCard
                    ? 'bg-slate-300 text-white cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#375AE7] to-indigo-600 text-white shadow-md hover:shadow-lg hover:scale-[1.02]'
                }`}
              >
                {savingCard ? 'Saving...' : 'Save Card'}
              </button>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="relative w-full max-w-6xl mx-4 bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-5 border-b bg-gradient-to-r from-indigo-50 via-white to-blue-50 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Sparkles size={18} className="text-[#375AE7]" />
                  Select Subscription Plan
                </h2>
                <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                  <Info size={14} className="text-slate-400" />
                  Choose a plan that best fits your requirements. You can
                  upgrade anytime.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-xl hover:bg-white border border-slate-200 transition"
              >
                <X size={18} className="text-slate-600" />
              </button>
            </div>
            <div className="px-4 md:px-8 py-6 max-h-[75vh] overflow-y-auto">
              <PricingPlanCards
                planId={mySub?.plan_id}
                selectedPlan={selectedPlan}
                onSelectPlan={(plan) => {
                  setSelectedPlan(plan?.id ?? plan);
                }}
              />
            </div>
            <div className="px-6 py-5 border-t bg-slate-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <p>Secure selection • Changes apply immediately</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={() => {
                    setLoading(true);
                    handleChangeSubscription();
                  }}
                  disabled={loading}
                  className="group flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 group-hover:scale-110 transition" />
                      Update Now
                    </>
                  )}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 hover:border-slate-300 shadow-sm hover:shadow transition-all duration-300 w-full sm:w-auto"
                >
                  <X className="w-4 h-4" />
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInvoiceModal && invoiceDetail && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowInvoiceModal(false)}
          />
          <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-5 border-b bg-gradient-to-r from-indigo-50 via-white to-blue-50 flex items-start justify-between">
              <div className="flex gap-3 items-center">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#375AE7] to-indigo-600 text-white flex items-center justify-center">
                  <ReceiptText size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {invoiceDetail.invoice_number}
                  </h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {formatDate(invoiceDetail.period_start)} –{' '}
                    {formatDate(invoiceDetail.period_end)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${invoiceStatusUI(
                    invoiceDetail.status,
                  )}`}
                >
                  {invoiceDetail.status}
                </span>
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="p-2 rounded-xl hover:bg-white border border-slate-200 transition"
                >
                  <X size={18} className="text-slate-600" />
                </button>
              </div>
            </div>
            <div className="px-6 py-5 max-h-[70vh] overflow-y-auto space-y-5">
              {detailLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 size={28} className="animate-spin text-[#375AE7]" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">Subtotal</p>
                      <p className="mt-1 font-bold text-slate-900">
                        {formatCents(invoiceDetail.subtotal_cents)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">
                        {invoiceDetail.tax_label || 'Tax'}
                      </p>
                      <p className="mt-1 font-bold text-slate-900">
                        {formatCents(invoiceDetail.tax_amount_cents)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-emerald-50/70 p-3">
                      <p className="text-xs text-emerald-600">Total</p>
                      <p className="mt-1 font-bold text-emerald-700">
                        {formatCents(invoiceDetail.total_cents)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">Volume</p>
                      <p className="mt-1 font-bold text-slate-900">
                        {invoiceDetail.volume ?? '-'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-400 mb-3">
                      Line items
                    </h3>
                    {invoiceDetail.line_items &&
                    invoiceDetail.line_items.length > 0 ? (
                      <div className="overflow-x-auto border border-slate-200 rounded-2xl">
                        <table className="w-full">
                          <thead>
                            <tr className="text-left text-xs text-slate-500 bg-slate-50 border-b">
                              <th className="px-4 py-3">Description</th>
                              <th className="px-4 py-3 text-right">Qty</th>
                              <th className="px-4 py-3 text-right">Unit</th>
                              <th className="px-4 py-3 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoiceDetail.line_items.map((li, idx) => (
                              <tr
                                key={idx}
                                className="border-b last:border-none"
                              >
                                <td className="px-4 py-3">
                                  <p className="font-medium text-slate-800 capitalize">
                                    {li.description || li.code}
                                  </p>
                                  {li.code && li.description && (
                                    <p className="text-xs text-slate-400">
                                      {li.code}
                                    </p>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right text-slate-600">
                                  {li.quantity}
                                </td>
                                <td className="px-4 py-3 text-right text-slate-600">
                                  {formatCents(li.unit_price_cents)}
                                </td>
                                <td
                                  className={`px-4 py-3 text-right font-semibold ${
                                    (li.amount_cents ?? 0) < 0
                                      ? 'text-emerald-600'
                                      : 'text-slate-900'
                                  }`}
                                >
                                  {formatCents(li.amount_cents)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                        No line item breakdown available for this invoice.
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 flex justify-between">
                      <span className="text-slate-500">Issued</span>
                      <span className="font-semibold text-slate-800">
                        {formatDate(invoiceDetail.issued_at)}
                      </span>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 flex justify-between">
                      <span className="text-slate-500">Paid</span>
                      <span className="font-semibold text-slate-800">
                        {formatDate(invoiceDetail.paid_at)}
                      </span>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 flex justify-between">
                      <span className="text-slate-500">Charge attempts</span>
                      <span className="font-semibold text-slate-800">
                        {invoiceDetail.charge_attempt_count ?? 0}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-slate-500">
                {invoiceDetail.currency || 'AUD'} ·{' '}
                {invoiceDetail.pricing_mode || 'threshold'} pricing
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold hover:bg-slate-100 transition text-sm"
                >
                  Close
                </button>
                <button
                  onClick={() => downloadInvoicePdf(invoiceDetail.id)}
                  disabled={!invoiceDetail.pdf_available}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#375AE7] to-indigo-600 text-white font-semibold shadow-md hover:shadow-lg transition disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                >
                  <FileDown size={16} />
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editPackagesOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setEditPackagesOpen(false)}
          />
          <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-5 border-b bg-gradient-to-r from-indigo-50 via-white to-blue-50 flex items-start justify-between">
              <div className="flex gap-3 items-center">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#375AE7] to-indigo-600 text-white flex items-center justify-center">
                  <ScanLine size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Manage Verification Features
                  </h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Choose which screening packages run for your business.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setEditPackagesOpen(false)}
                className="p-2 rounded-xl hover:bg-white border border-slate-200 transition"
              >
                <X size={18} className="text-slate-600" />
              </button>
            </div>
            <div className="px-6 py-5 max-h-[65vh] overflow-y-auto space-y-4">
              <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                  <UserCheck size={18} className="text-[#375AE7]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-800">Identity</p>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                      <CheckCircle size={11} />
                      Always included
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Identity verification is always enabled and cannot be turned
                    off.
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400 mb-2">
                  Optional screening packages
                </p>
                <div className="space-y-2.5">
                  {['pep', 'sanctions', 'media', 'watchlist'].map((code) => {
                    const meta = PACKAGE_META[code];
                    const Icon = meta?.icon || ScanLine;
                    const isOn = draftItemCodes.includes(code);
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => togglePackage(code)}
                        className={`w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition ${
                          isOn
                            ? 'border-[#375AE7]/40 bg-blue-50/70'
                            : 'border-slate-200 bg-white hover:bg-slate-50'
                        }`}
                      >
                        <div
                          className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
                            isOn
                              ? 'bg-[#375AE7]/10 text-[#375AE7]'
                              : 'bg-slate-100 text-slate-400'
                          }`}
                        >
                          <Icon size={18} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-800">
                            {meta?.name || code}
                          </p>
                          <p className="text-xs text-slate-500">
                            {meta?.desc || 'Verification package'}
                          </p>
                        </div>
                        <span
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
                            isOn ? 'bg-[#375AE7]' : 'bg-slate-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                              isOn ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {packageDefaults?.has_override && (
                <button
                  type="button"
                  onClick={handleResetPackages}
                  disabled={resettingPackages}
                  className="w-full flex items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resettingPackages ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Resetting…
                    </>
                  ) : (
                    <>
                      <X size={16} />
                      Reset to platform defaults
                    </>
                  )}
                </button>
              )}
            </div>
            <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-slate-500">
                {draftItemCodes.length > 0
                  ? `${draftItemCodes.length} extra${draftItemCodes.length > 1 ? 's' : ''} selected`
                  : 'Identity only'}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditPackagesOpen(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold hover:bg-slate-100 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePackages}
                  disabled={savingPackages}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#375AE7] to-indigo-600 text-white font-semibold shadow-md hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {savingPackages ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPricingPlan;
